import { Download, Upload } from "lucide-react";
import { type ChangeEvent, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  IMPORT_CSV_TEMPLATE,
  type ImportRow,
  parseImportCsv,
  SAMPLE_IMPORT_CSV,
} from "@/mocks/import-work-orders";

interface CsvUploadPanelProps {
  onParsed: (rows: ImportRow[], fileName: string) => void;
}

function downloadSampleCsv() {
  const blob = new Blob([SAMPLE_IMPORT_CSV], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "work-order-import-sample.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function CsvUploadPanel({ onParsed }: CsvUploadPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      onParsed(parseImportCsv(text), file.name);
    };
    reader.readAsText(file);
    event.target.value = "";
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Upload CSV</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          Expected columns, in order:{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{IMPORT_CSV_TEMPLATE}</code>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={() => inputRef.current?.click()}>
            <Upload className="size-4" />
            Choose CSV file
          </Button>
          <Button type="button" variant="outline" onClick={downloadSampleCsv}>
            <Download className="size-4" />
            Download sample CSV
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </CardContent>
    </Card>
  );
}
